import React, { ReactNode } from 'react';
import { Table, List, Grid, Card, Empty, Skeleton } from 'antd';
import type { ColumnsType } from 'antd/es/table';

const { useBreakpoint } = Grid;

/**
 * Elenco che cambia forma con lo schermo.
 *
 * Su desktop è una tabella. Sul telefono una tabella o si stringe fino a
 * essere illeggibile o costringe a scorrere di lato per ogni riga: lì
 * diventa una scheda per elemento, dove ogni dato ha la sua riga.
 */
export function Elenco<T extends object>({
  righe, colonne, loading, rowKey, onRowClick, vuoto, scheda, pageSize = 20, scrollX = 760,
}: {
  righe: T[];
  colonne: ColumnsType<T>;
  loading?: boolean;
  rowKey: (r: T) => React.Key;
  onRowClick?: (r: T) => void;
  vuoto?: ReactNode;
  /** Come disegnare un elemento sul telefono */
  scheda: (r: T) => ReactNode;
  pageSize?: number;
  scrollX?: number;
}) {
  const schermo = useBreakpoint();
  const mobile = !schermo.md;

  const nessunDato = vuoto ?? (
    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nessun elemento" />
  );

  if (mobile) {
    if (loading) {
      return (
        <div style={{ padding: 14 }}>
          {[0, 1, 2, 3].map(i => (
            <Card size="small" key={i} style={{ marginBottom: 10 }}>
              <Skeleton active paragraph={{ rows: 2 }} title={{ width: '45%' }} />
            </Card>
          ))}
        </div>
      );
    }
    return (
      <List
        dataSource={righe}
        locale={{ emptyText: nessunDato }}
        pagination={righe.length > pageSize ? { pageSize, style: { padding: '0 14px' } } : false}
        renderItem={r => (
          <List.Item
            key={rowKey(r)}
            onClick={() => onRowClick?.(r)}
            style={{
              padding: '14px 16px',
              cursor: onRowClick ? 'pointer' : undefined,
              display: 'block',
            }}
          >
            {scheda(r)}
          </List.Item>
        )}
      />
    );
  }

  return (
    <Table<T>
      rowKey={rowKey}
      columns={colonne}
      dataSource={righe}
      loading={loading}
      onRow={r => ({
        onClick: () => onRowClick?.(r),
        style: onRowClick ? { cursor: 'pointer' } : undefined,
      })}
      pagination={{
        pageSize,
        hideOnSinglePage: true,
        showTotal: (t, r) => `${r[0]}–${r[1]} di ${t}`,
        style: { padding: '0 20px' },
      }}
      locale={{ emptyText: nessunDato }}
      scroll={{ x: scrollX }}
    />
  );
}

/** Riga etichetta/valore per le schede su telefono. */
export function Voce({ etichetta, children }: { etichetta: string; children: ReactNode }) {
  return (
    <div className="voce">
      <span className="voce-k">{etichetta}</span>
      <span className="voce-v">{children}</span>
    </div>
  );
}
